import React, { useRef, useCallback } from 'react';
import type { DrawingPath } from '../../../../types/photo.types';

interface DrawingCanvasProps {
  width: number;
  height: number;
  paths: DrawingPath[];
  activeTool: 'draw' | 'pin' | null;
  color: string;
  strokeWidth: number;
  onPathComplete: (path: DrawingPath) => void;
  onTap?: (x: number, y: number) => void;
}

let pathIdCounter = 0;

/** Ramer-Douglas-Peucker point decimation */
function decimatePoints(points: Array<{ x: number; y: number }>, epsilon: number): Array<{ x: number; y: number }> {
  if (points.length <= 2) return points;
  let maxDist = 0;
  let maxIdx = 0;
  const first = points[0];
  const last = points[points.length - 1];
  for (let i = 1; i < points.length - 1; i++) {
    const d = Math.abs(
      (last.y - first.y) * points[i].x - (last.x - first.x) * points[i].y + last.x * first.y - last.y * first.x
    ) / Math.hypot(last.y - first.y, last.x - first.x);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }
  if (maxDist > epsilon) {
    const left = decimatePoints(points.slice(0, maxIdx + 1), epsilon);
    const right = decimatePoints(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
}

export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  width,
  height,
  paths,
  activeTool,
  color,
  strokeWidth,
  onPathComplete,
  onTap,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const drawingRef = useRef<Array<{ x: number; y: number }>>([]);
  const isDrawingRef = useRef(false);

  const toNormalized = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (activeTool === 'draw') {
      isDrawingRef.current = true;
      const pt = toNormalized(e.clientX, e.clientY);
      drawingRef.current = [pt];
      (e.target as Element).setPointerCapture(e.pointerId);
    }
  }, [activeTool, toNormalized]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDrawingRef.current || activeTool !== 'draw') return;
    const pt = toNormalized(e.clientX, e.clientY);
    drawingRef.current.push(pt);
    // Force re-render for live preview
    const svg = svgRef.current;
    if (svg) {
      const preview = svg.querySelector('.dc-preview') as SVGPolylineElement;
      if (preview) {
        preview.setAttribute('points', drawingRef.current.map(p => `${p.x},${p.y}`).join(' '));
      }
    }
  }, [activeTool, toNormalized]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (isDrawingRef.current && activeTool === 'draw') {
      isDrawingRef.current = false;
      const raw = drawingRef.current;
      if (raw.length >= 2) {
        const decimated = decimatePoints(raw, 0.002);
        onPathComplete({
          id: `path-${++pathIdCounter}`,
          points: decimated,
          color,
          strokeWidth,
        });
      }
      drawingRef.current = [];
      const svg = svgRef.current;
      if (svg) {
        const preview = svg.querySelector('.dc-preview') as SVGPolylineElement;
        if (preview) preview.setAttribute('points', '');
      }
    } else if (activeTool === 'pin') {
      const pt = toNormalized(e.clientX, e.clientY);
      onTap?.(pt.x, pt.y);
    }
  }, [activeTool, color, strokeWidth, onPathComplete, onTap, toNormalized]);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        touchAction: 'none',
        cursor: activeTool === 'draw' ? 'crosshair' : activeTool === 'pin' ? 'copy' : 'default',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {paths.map(p => (
        <polyline
          key={p.id}
          points={p.points.map(pt => `${pt.x},${pt.y}`).join(' ')}
          stroke={p.color}
          strokeWidth={p.strokeWidth / 1000}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      <polyline
        className="dc-preview"
        points=""
        stroke={color}
        strokeWidth={strokeWidth / 1000}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
